/**
 * Missing Assets Report API
 *
 * Generates reports showing file status for scheduled content
 */

import helper from './helper'
import axios from '@/axios'

/**
 * Get available channels for report
 */
export async function getReportChannels() {
  return await helper.get('/api/reports/channels')
}

/**
 * Get missing assets report
 *
 * @param {Object} params
 * @param {number} params.channelId - Channel ID
 * @param {string} params.startDate - Start date (YYYY-MM-DD)
 * @param {string} params.endDate - End date (YYYY-MM-DD)
 * @param {string} params.statusFilter - Filter by status (comma-separated)
 * @param {string} params.format - 'json' or 'csv'
 */
export async function getMissingAssetsReport(params) {
  return await helper.get('/api/reports/missing-assets', params)
}

/**
 * Download missing assets report as CSV
 *
 * @param {Object} params - request params (channelId, startDate, endDate, statusFilter)
 * @param {string} [channelName] - channel name for filename. Strongly recommended —
 *   without it every CSV downloads with the same generic name regardless of which
 *   channel it's from, making multi-channel triage painful.
 */
export async function downloadMissingAssetsReportCsv(params, channelName) {
  // Use axios with responseType: 'blob' for file downloads.
  // Axios defaults provide the auth header.
  const response = await axios.get('/api/reports/missing-assets', {
    params: { ...params, format: 'csv' },
    responseType: 'blob'
  })

  // Build filename: include channel slug (sanitised). Falls back to channelId if
  // channelName wasn't passed (older callers), then to a bare timestamp prefix.
  const slug = channelName
    ? channelName.replace(/[^\w]+/g, '-').replace(/^-+|-+$/g, '')
    : (params.channelId ? `channel-${params.channelId}` : 'unknown-channel')
  const filename = `missing-assets-${slug}-${params.startDate}-to-${params.endDate}.csv`

  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

/**
 * Asset status types
 */
export const ASSET_STATUS = {
  MISSING: 'missing',
  NOT_STARTED: 'not_started',
  QUEUED: 'queued',
  IN_QC: 'in_qc',
  QC_FAILED: 'qc_failed',
  QC_PASSED: 'qc_passed',
  PROCESSING: 'processing',
  READY: 'ready',
  OVERRIDDEN: 'overridden'
}

/**
 * Get status display info (label, color, icon)
 */
export function getStatusInfo(status) {
  const statusMap = {
    missing: { label: 'Missing', type: 'is-danger', icon: 'mdi-file-remove' },
    not_started: { label: 'Not Started', type: 'is-warning', icon: 'mdi-file-outline' },
    queued: { label: 'Queued', type: 'is-info', icon: 'mdi-playlist-play' },
    in_qc: { label: 'In QC', type: 'is-info', icon: 'mdi-magnify' },
    qc_failed: { label: 'QC Failed', type: 'is-danger', icon: 'mdi-close-circle' },
    qc_passed: { label: 'QC Passed', type: 'is-success', icon: 'mdi-check-circle' },
    processing: { label: 'Processing', type: 'is-info', icon: 'mdi-cog-sync' },
    ready: { label: 'Ready', type: 'is-success', icon: 'mdi-check-bold' },
    overridden: { label: 'Overridden', type: 'is-warning', icon: 'mdi-check-decagram' }
  }
  return statusMap[status] || { label: status, type: 'is-light', icon: 'mdi-help-circle' }
}

/**
 * Bulk-confirm a list of soft-linked episodes via the Traffic Agent
 * endpoint. Used by the Missing Assets Report's "Confirm All" button so an
 * operator can stamp all CONFIRM_NEEDED items in one click instead of
 * opening Edit Episode for each one. Backend gates each ep server-side
 * (soft_linked + pending_review + soft-seg done) and skips with reason
 * for any that fail the gate.
 */
export async function bulkConfirmEpisodes(episodeIds) {
  return await helper.post('/api/traffic-agent/bulk-confirm', { episodeIds })
}

export default {
  getReportChannels,
  getMissingAssetsReport,
  downloadMissingAssetsReportCsv,
  bulkConfirmEpisodes,
  ASSET_STATUS,
  getStatusInfo
}
